/**
 * Lot Group Card HTML Template
 *
 * Returns HTML string for the lot group details card displaying lot number, size, type, and facing information.
 * Used in the Lot & Elevation tab of the floor plan detail view.
 *
 * @module nativeInteractiveSiteMap/components/lotGroupCard.dom
 */

/**
 * Generates a single detail field with optional divider
 * @param {string} label - Field label (e.g., "LOT SIZE", "TYPE", "FACING")
 * @param {string} value - Field value to display
 * @param {boolean} showDivider - Whether to show divider before field (default: false)
 * @returns {string} HTML string for the detail field
 */
function getDetailFieldHtml(label, value, showDivider = false) {
  const divider = showDivider
    ? '<div class="nhs-c-lot-details-card__divider nhs-mq-lot-details-card__divider-medium nhs-u-shrink--0"></div>'
    : '';

  return `
    <div class="nhs-u-display--flex nhs-u-items-center nhs-u-flex--1-0-0">
      ${divider}
      <div class="nhs-u-flex-column nhs-u-items-flex-start nhs-u-gap--100">
        <p class="nhs-mq-lot-details-card__label-medium nhs-o-typography--sans-label-1-extra-extra-extra-small-regular nhs-u-color--black-900 nhs-u-margin-bottom--100">
          ${label}
        </p>
        <p class="nhs-c-lot-details-card__value nhs-mq-lot-details-card__value-medium nhs-o-typography--sans-body-1-small-regular nhs-u-color--black-1000">
          ${value}
        </p>
      </div>
    </div>
  `;
}

function normalizeDetailValue(value) {
  return typeof value === 'string' ? value.trim() : value;
}

function hasRenderableDetailValue(value) {
  const normalized = normalizeDetailValue(value);
  return Boolean(normalized) && normalized.toLowerCase() !== 'n/a';
}

/**
 * Generates the Lot Group Card HTML
 * @param {Object} details - Details object with lotNumber, group, size, type, facing (optional)
 * @returns {string} HTML string for the lot details card
 */
export default function getLotGroupCardHtml(details = {}) {
  const lotNumber = details.lotNumber || null;
  const group = details.group || null;
  const size = details.size || null;
  const type = details.type || null;
  const facing = details.facing || null;

  // Build conditional HTML sections - only render if data is provided
  // showDivider is true only when there is a visible field before the current one.
  const normalizedSize = normalizeDetailValue(size);
  const normalizedType = normalizeDetailValue(type);
  const normalizedFacing = normalizeDetailValue(facing);

  const hasSize = hasRenderableDetailValue(size);
  const hasType = hasRenderableDetailValue(type);
  const hasFacing = hasRenderableDetailValue(facing);

  const sizeHtml = hasSize ? getDetailFieldHtml('LOT SIZE', normalizedSize, false) : '';
  const typeHtml = hasType ? getDetailFieldHtml('TYPE', normalizedType, hasSize) : '';
  const facingHtml = hasFacing
    ? getDetailFieldHtml('FACING', normalizedFacing, hasSize || hasType)
    : '';

  // Build title - only render if lotNumber or group is provided
  const titleText = [
    lotNumber || '',
    group || '',
  ].filter(Boolean).join(' ');

  const titleHtml =
    titleText
      ? `
        <!-- Title -->
        <div class="nhs-u-margin-bottom--300">
          <h3 class="nhs-c-lot-details-card__title nhs-mq-lot-details-card__title-medium nhs-u-color--black-1000">
            ${titleText}
          </h3>
        </div>
      `
      : '';

  return `
    <div class="nhs-c-lot-details-card">
        ${titleHtml}
        <!-- Details Row -->
        <div class="nhs-c-lot-details-card__info nhs-mq-lot-details-card__info-medium nhs-u-display--flex nhs-u-items-center nhs-u-width--full">
          ${sizeHtml}
          ${typeHtml}
          ${facingHtml}
        </div>
    </div>
  `;
}
